import React, { useCallback } from 'react';
import classnames from 'classnames';
import { QrReader } from 'react-qr-reader';
import logger from 'common/utils/logger';
import { useOAT } from 'common/providers/oatProvider';
import { useConfig } from 'common/providers/configProvider';
import { scanToJoinTable } from 'common/utils/scan-qr-to-join-table';
import { useContent } from 'content/content-provider';
import { Typography } from '@material-ui/core';
import useIdValidation from 'loyalty/hooks/useIdValidation';
import useStyles from './QrCodeReaderModal.styles';
import { NewModal } from '../Modal';
import { AlertBanner } from '../AlertBanner';

interface QrCodeReaderModalProps {
  open: boolean;
  closeQrScanner: () => void;
  closeScanModal?: () => void;
}

const QrCodeReaderModal = ({ open, closeQrScanner, closeScanModal }: QrCodeReaderModalProps) => {
  const classes = useStyles();
  const { error, queryTableStatus } = useOAT();
  const { paymentMode } = useConfig();
  const [content] = useContent();

  const handleClose = useCallback(() => {
    closeQrScanner();
    if (closeScanModal) closeScanModal();
  }, [closeQrScanner, closeScanModal]);

  const { errorMessage } = useIdValidation('', handleClose);

  const handleScan = useCallback(
    (result) => {
      if (result) {
        logger.info({ msg: 'QR code successfully read via QR reader' });
        scanToJoinTable(result.toString(), queryTableStatus, paymentMode);
      }
    },
    [queryTableStatus, paymentMode]
  );

  const errorBanner = classnames(classes.errorBanner, {
    [classes.fadeIn]: errorMessage || error,
  });

  return (
    <NewModal
      fullScreen
      fullWidth
      showHeader
      open={open}
      displayCaloriesBanner={false}
      handleClose={closeQrScanner}
      aria-label="QR Scanner Modal"
      className={classes.dialogContent}
    >
      {(errorMessage || error) && (
        <AlertBanner className={errorBanner} variant="error" withIcon>
          <Typography className={classes.errorMessage} variant="h4">
            {errorMessage || content.qrReader.error}
          </Typography>
        </AlertBanner>
      )}
      <QrReader
        onResult={(result) => handleScan(result)}
        videoStyle={{ objectFit: 'cover' }}
        className={classes.qrReader}
        constraints={{ facingMode: { exact: 'environment' } }}
      />
    </NewModal>
  );
};

export default QrCodeReaderModal;
